<template>
  <div class="erc-ai-advisor-container theme-dark" id="erc-ai-advisor-container">

    <!-- Header -->
    <div class="erc-ai-advisor__header">
      <div class="erc-ai-advisor__header-icon">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
      </div>
      <div>
        <h3 class="erc-ai-advisor__title">{{ t('aiAdvisor.title') }}</h3>
        <p class="erc-ai-advisor__subtitle">{{ t('aiAdvisor.subtitle') }}</p>
      </div>
    </div>

    <!-- Input Row -->
    <div class="erc-ai-advisor__input-row">
      <input
        v-model="query"
        type="text"
        class="erc-ai-advisor__input"
        :placeholder="t('aiAdvisor.input.placeholder')"
        :disabled="loading"
        @keyup.enter="analyzeGains"
        maxlength="200"
      />
      <button
        class="erc-ai-advisor__btn"
        :class="{ 'erc-ai-advisor__btn--loading': loading }"
        :disabled="loading || !query.trim()"
        @click="analyzeGains"
      >
        <svg v-if="!loading" class="erc-ai-advisor__btn-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
        <svg v-else class="erc-ai-advisor__spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle class="erc-ai-advisor__spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="erc-ai-advisor__spinner-fill" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
        </svg>
        <span>{{ loading ? t('aiAdvisor.button.analysing') : t('aiAdvisor.button.analyse') }}</span>
      </button>
    </div>

    <!-- Results Area -->
    <div v-if="hasResult || error" class="erc-ai-advisor__results">

      <!-- Error -->
      <div v-if="error" class="erc-ai-advisor__error">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
        {{ error }}
      </div>

      <!-- Vehicle Card -->
      <div v-if="vehicleData" class="erc-ai-advisor__vehicle-card">
        <div class="vehicle-card__columns">
          <div class="vehicle-card__left">
            <div class="brand-logo" v-if="vehicleData.brand && vehicleData.brand.logo">
              <img :src="getFullAssetUrl(vehicleData.brand.logo)" :alt="vehicleData.brand.name" />
            </div>
            <div class="vehicle-card__info">
              <div class="vehicle-card__name-line">
                <span class="vehicle-card__brand">{{ vehicleData.brand ? vehicleData.brand.name : '' }}</span>
                <span class="vehicle-card__model">{{ vehicleData.model ? vehicleData.model.name : '' }}</span>
              </div>
              <div class="vehicle-card__gen-line" v-if="vehicleData.generation">
                <span class="vehicle-card__gen-name">{{ vehicleData.generation.name }}</span>
                <span class="vehicle-card__gen-years">({{ vehicleData.generation.year }} – {{ vehicleData.generation.yearend || t('remapInfo.generation.presentYear') }})</span>
              </div>
              <div class="vehicle-card__engine" v-if="vehicleData.engine">{{ vehicleData.engine.name }}</div>
            </div>
          </div>
          <div class="vehicle-card__right">
            <img v-if="vehicleData.generation && vehicleData.generation.image" :src="getFullAssetUrl(vehicleData.generation.image)" :alt="t('remapInfo.alt.vehicleImage')" class="vehicle-card__photo" />
            <img v-else :src="fallbackImageUrl" :alt="t('remapInfo.alt.vehicleImagePlaceholder')" class="vehicle-card__photo" />
          </div>
        </div>
        <div class="erc-ai-advisor__db-row">
          <div class="erc-ai-advisor__db-badge">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            {{ t('aiAdvisor.badge.vehicleMatched') }}
          </div>
          <a
            v-if="catalogueUrl"
            :href="'/vehicle-catalogue/?vehicle=' + catalogueUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="erc-ai-advisor__catalogue-link"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            {{ t('aiAdvisor.link.viewInCatalogue') }}
          </a>
        </div>
      </div>

      <!-- Suggestions (no DB match) -->
      <div v-if="!vehicleData && suggestions.length" class="erc-ai-advisor__suggestions">
        <div class="erc-ai-advisor__suggestions-label">{{ t('aiAdvisor.suggestions.label') }}</div>
        <div class="erc-ai-advisor__suggestions-list">
          <button
            v-for="(s, i) in suggestions"
            :key="i"
            class="erc-ai-advisor__suggestion-chip"
            @click="selectSuggestion(s)"
          >
              {{ s.label }}
          </button>
        </div>
      </div>

      <!-- AI Response -->
      <div v-if="aiResponse" class="erc-ai-advisor__response">
        <div class="erc-ai-advisor__response-label">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          {{ t('aiAdvisor.response.label') }}
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="erc-ai-advisor__response-body" v-html="formattedResponse"></div>
        <div class="erc-ai-advisor__disclaimer">
          <span class="erc-ai-advisor__disclaimer-note">{{ t('aiAdvisor.disclaimer.note') }}</span>
          {{ t('aiAdvisor.disclaimer.text') }}
          <a href="https://ecuremapcenter.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" class="erc-ai-advisor__disclaimer-link">{{ t('aiAdvisor.disclaimer.link') }}</a>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { getFullAssetUrl } from '@/utils/urlUtils.js';
import { t } from '@/utils/i18n';

export default {
  name: 'AiTuningAdvisor',

  data() {
    return {
      query:        '',
      loading:      false,
      aiResponse:   null,
      vehicleData:  null,
      catalogueUrl: null,
      suggestions:  [],
      error:        null,
    };
  },

  computed: {
    fallbackImageUrl() {
      const rawTypeId = this.vehicleData?.vehicleType;
      const typeId = ( ! isNaN( parseInt( rawTypeId, 10 ) ) && parseInt( rawTypeId, 10 ) > 0 )
        ? parseInt( rawTypeId, 10 ) : 1;
      if ( process.env.NODE_ENV === 'development' ) {
        return `/img/nophoto-${typeId}.webp`;
      }
      const pluginUrl = window.ercData?.plugin_url ?? '/';
      const base = pluginUrl.endsWith( '/' ) ? pluginUrl : pluginUrl + '/';
      return `${base}dist/img/nophoto-${typeId}.webp`;
    },
    hasResult() {
      return this.aiResponse !== null || this.vehicleData !== null || this.suggestions.length > 0;
    },
    formattedResponse() {
      if ( ! this.aiResponse ) return '';
      return this.aiResponse
        // **bold** → <strong>
        .replace( /\*\*(.*?)\*\*/g, '<strong>$1</strong>' )
        // Double newlines → paragraphs
        .split( '\n\n' )
        .map( p => `<p>${p.replace( /\n/g, '<br>' )}</p>` )
        .join( '' );
    },
  },

  methods: {
    t,
    getFullAssetUrl,

    async analyzeGains() {
      const trimmed = this.query.trim();
      if ( ! trimmed || this.loading ) return;

      this.loading      = true;
      this.aiResponse   = null;
      this.vehicleData  = null;
      this.catalogueUrl = null;
      this.suggestions  = [];
      this.error        = null;

      try {
        const formData = new URLSearchParams();
        formData.append( 'action', 'erc_ai_tuning_advisor' );
        formData.append( 'nonce',  window.ercData?.nonce ?? '' );
        formData.append( 'query',  trimmed );

        const response = await fetch( window.ercData?.ajax_url ?? '/wp-admin/admin-ajax.php', {
          method:  'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body:    formData.toString(),
        } );

        // Always parse JSON — WordPress AJAX always returns HTTP 200 even for errors;
        // the success/failure is signalled inside the JSON body.
        const json = await response.json();

        if ( ! json.success ) {
          this.error = json.data?.message ?? t('aiAdvisor.error.generic');
          return;
        }

        this.aiResponse   = json.data?.ai_response   ?? null;
        this.vehicleData  = json.data?.vehicle_data  ?? null;
        this.catalogueUrl = json.data?.catalogue_url ?? null;
        this.suggestions  = json.data?.suggestions   ?? [];

      } catch ( err ) {
        this.error = t('aiAdvisor.error.unavailable');
        console.error( '[ERC AI Advisor]', err );
      } finally {
        this.loading = false;
      }
    },

    selectSuggestion( s ) {
      this.query = s.query;
      this.$nextTick( () => this.analyzeGains() );
    },
  },
};
</script>

<style scoped>
/* ── Container ── */
.erc-ai-advisor-container {
  font-family: inherit;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  border-radius: 16px;
  padding: 28px 28px 32px;
  box-sizing: border-box;
}

/* Light theme */
.erc-ai-advisor-container.theme-light {
  background: #f4f6f9;
  border: 1px solid #e0e4ea;
  color: #1a1d23;
}

/* ── Header ── */
.erc-ai-advisor__header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}

.erc-ai-advisor__header-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--erc-color-oem, #00aeff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.erc-ai-advisor__header-icon svg {
  width: 22px;
  height: 22px;
  color: #fff;
}

.erc-ai-advisor__title {
  margin: 0 0 2px;
  font-size: 22px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 1.2;
}

.theme-light .erc-ai-advisor__title { color: #1a1d23; }

.erc-ai-advisor__subtitle {
  margin: 0;
  font-size: 0.78rem;
  opacity: 0.55;
  letter-spacing: 0.03em;
}

/* ── Input Row ── */
.erc-ai-advisor__input-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.erc-ai-advisor__input {
  flex: 1 1 220px;
  border-radius: 30px;
  padding: 14px 18px;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  box-sizing: border-box;
}

.theme-light .erc-ai-advisor__input {
  background: #fff;
  border: 1.5px solid #d0d5dc;
  color: #1a1d23;
}

.erc-ai-advisor__input::placeholder {
  opacity: 0.45;
}

.erc-ai-advisor__input:focus {
  border-color: var(--erc-color-oem, #00aeff);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--erc-color-oem, #00aeff) 18%, transparent);
}

.erc-ai-advisor__input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Button ── */
.erc-ai-advisor__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-radius: 30px;
  border: none;
  background: var(--erc-color-oem, #00aeff);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, opacity 0.2s, transform 0.1s;
}

.erc-ai-advisor__btn:hover:not(:disabled) {
  filter: brightness(1.1);
}

.erc-ai-advisor__btn:active:not(:disabled) {
  transform: scale(0.98);
}

.erc-ai-advisor__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.erc-ai-advisor__btn-icon {
  width: 18px;
  height: 18px;
}

/* ── Spinner ── */
.erc-ai-advisor__spinner {
  width: 18px;
  height: 18px;
  animation: erc-ai-spin 0.8s linear infinite;
}

.erc-ai-advisor__spinner-track { opacity: 0.25; }
.erc-ai-advisor__spinner-fill  { opacity: 0.75; }

@keyframes erc-ai-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ── Results ── */
.erc-ai-advisor__results {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ── Error ── */
.erc-ai-advisor__error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
}

.theme-light .erc-ai-advisor__error {
  background: #fff0f0;
  border: 1px solid #fad0d0;
  color: #c0392b;
}

.erc-ai-advisor__error svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Vehicle Card — reuses RemapInfo.vue CSS classes for the info block ── */
.erc-ai-advisor__vehicle-card {
  border-radius: 12px;
  padding: 18px 20px 14px;
  overflow: hidden;
}

.theme-light .erc-ai-advisor__vehicle-card {
  background: #fff;
  border: 1px solid #e0e4ea;
}

.erc-ai-advisor__db-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.erc-ai-advisor__db-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 20px;
  letter-spacing: 0.02em;
}

.erc-ai-advisor__catalogue-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 20px;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s;
  margin-left: auto;
}

.erc-ai-advisor__catalogue-link svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* ── Vehicle Card two-column layout ── */
.vehicle-card__columns {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.vehicle-card__left {
  flex: 0 0 auto;
  min-width: 160px;
  max-width: 300px;
}

.vehicle-card__right {
  flex: 1 1 auto;
  overflow: hidden;
  border-radius: 8px;
}

.vehicle-card__photo {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  object-fit: contain;
  max-height: 300px;
  margin-top: -20px;
}

.brand-logo img {
  max-width: 60px;
  border: none;
  padding: 0;
  margin-bottom: 15px;
  display: block;
}

.vehicle-card__name-line {
  font-size: 1.4rem;
  margin-bottom: 5px;
  line-height: 1.3;
}

.vehicle-card__brand,
.vehicle-card__model {
  font-weight: 700;
}

.vehicle-card__brand {
  margin-right: 5px;
}

.vehicle-card__gen-line {
  font-size: 1rem;
  margin-bottom: 4px;
  line-height: 1.4;
  font-weight: 700;
}

.vehicle-card__gen-name {
  margin-right: 5px;
}

.theme-light .vehicle-card__gen-line { color: #555b66; }

.vehicle-card__engine {
  font-size: 1rem;
  margin-top: 2px;
}

.theme-light .vehicle-card__engine { color: #555b66; }

@media (max-width: 480px) {
  .vehicle-card__columns {
    flex-direction: column;
  }
  .vehicle-card__left {
    max-width: 100%;
  }
}

.theme-light .erc-ai-advisor__db-badge {
  background: #e8f8e8;
  color: #27ae60;
  border: 1px solid #b2dfb2;
}

.theme-light .erc-ai-advisor__catalogue-link {
  background: #e8f0fc;
  color: #00aeff;
  border: 1px solid #00aeff;
}
.theme-light .erc-ai-advisor__catalogue-link:hover {
  background: #d6e4f7;
  border-color: #00aeff;
}

.erc-ai-advisor__db-badge svg {
  width: 14px;
  height: 14px;
}

/* ── AI Response ── */
.erc-ai-advisor__response {
  border-radius: 12px;
  padding: 20px 22px;
}

.theme-light .erc-ai-advisor__response {
  background: #fff;
  border: 1px solid #e0e4ea;
}

.erc-ai-advisor__response-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 14px;
  color: var(--erc-color-oem, #00aeff);
}

.erc-ai-advisor__response-label svg {
  width: 15px;
  height: 15px;
}

.erc-ai-advisor__response-body {
  font-size: 1rem;
  line-height: 1.75;
  font-family: 'IBM Plex Sans', sans-serif;
}

.theme-light .erc-ai-advisor__response-body { color: #3a3f4a; }

.erc-ai-advisor__response-body :deep(p) {
  margin: 0 0 0.85em;
}

.erc-ai-advisor__response-body :deep(p:last-child) {
  margin-bottom: 0;
}

.erc-ai-advisor__response-body :deep(strong) {
  font-weight: 700;
}

.theme-light .erc-ai-advisor__response-body :deep(strong) { color: #1a1d23; }

/* ── Legal Disclaimer ── */
.erc-ai-advisor__disclaimer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid;
  font-size: 0.75rem;
  line-height: 1.6;
}

.theme-light .erc-ai-advisor__disclaimer {
  color: #6b7280;
  border-top-color: #e0e4ea;
}

.erc-ai-advisor__disclaimer-note {
  font-weight: 700;
  color: #00aeff;
}

.erc-ai-advisor__disclaimer-link {
  font-weight: 700;
  color: #00aeff;
  text-decoration: none;
  font-size: 0.75rem;
}
.erc-ai-advisor__disclaimer-link:hover {
  text-decoration: underline;
}
.theme-light .erc-ai-advisor__disclaimer-link:hover { color: #1a1d23; }

/* ── Responsive ── */
@media (max-width: 540px) {
  .erc-ai-advisor-container {
    padding: 20px 16px 24px;
    border-radius: 12px;
  }
  .erc-ai-advisor__input-row {
    flex-direction: column;
  }
  .erc-ai-advisor__btn {
    width: 100%;
    justify-content: center;
  }
}

/* ── Suggestions (no DB match) ── */
.erc-ai-advisor__suggestions {
  border-radius: 12px;
  padding: 18px 20px;
}

.theme-light .erc-ai-advisor__suggestions {
  background: #fff;
  border: 1px solid #e0e4ea;
}

.erc-ai-advisor__suggestions-label {
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.theme-light .erc-ai-advisor__suggestions-label {
  color: #555b66;
}

.erc-ai-advisor__suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.erc-ai-advisor__suggestion-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 30px;
  border: 1.5px solid var(--erc-color-oem, #00aeff);
  background: transparent;
  color: var(--erc-color-oem, #00aeff);
  font-size: 0.88rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  text-align: left;
}

.erc-ai-advisor__suggestion-chip:hover {
  background: var(--erc-color-oem, #00aeff);
  color: #fff;
}

/* ════════════════════════════════════════════════════════════════════
   DARK THEME — applied via .theme-dark on the root container.
   Mirrors the price-list calculator's darker palette while keeping the
   vehicle card and AI response card on white so brand logos / vehicle
   photos (which are on white backgrounds) blend correctly.
   To revert: change `theme-dark` back to `theme-light` on the root div.
   ════════════════════════════════════════════════════════════════════ */

/* Outer container — flat dark blue-grey */
.erc-ai-advisor-container.theme-dark {
  background: #0D131E;
  border: 1px solid #3a4150;
  color: #e6e8ec;
}

.theme-dark .erc-ai-advisor__title    { color: #ffffff; }
.theme-dark .erc-ai-advisor__subtitle { color: #9aa3b2; opacity: 1; }

/* Input — dark surface */
.theme-dark .erc-ai-advisor__input {
  background: #161F32;
  border: 1.5px solid #3a4150;
  color: #e6e8ec;
}
.theme-dark .erc-ai-advisor__input::placeholder {
  color: #9aa3b2;
  opacity: 1;
}

/* Error — dark variant */
.theme-dark .erc-ai-advisor__error {
  background: #3a1f23;
  border: 1px solid #6b2a30;
  color: #ff6b6b;
}

/* Vehicle card stays WHITE (photos/logos rely on white backdrop) */
.theme-dark .erc-ai-advisor__vehicle-card {
  background: #ffffff;
  border: 1px solid #e0e4ea;
  color: #1a1d23;
}
.theme-dark .vehicle-card__gen-line { color: #555b66; }
.theme-dark .vehicle-card__engine   { color: #555b66; }

.theme-dark .erc-ai-advisor__db-badge {
  background: #e8f8e8;
  color: #27ae60;
  border: 1px solid #b2dfb2;
}
.theme-dark .erc-ai-advisor__catalogue-link {
  background: #e8f0fc;
  color: #00aeff;
  border: 1px solid #00aeff;
}
.theme-dark .erc-ai-advisor__catalogue-link:hover {
  background: #d6e4f7;
  border-color: #00aeff;
}

/* AI response card stays WHITE for body legibility */
.theme-dark .erc-ai-advisor__response {
  background: #ffffff;
  border: 1px solid #e0e4ea;
}
.theme-dark .erc-ai-advisor__response-body          { color: #3a3f4a; }
.theme-dark .erc-ai-advisor__response-body :deep(strong) { color: #1a1d23; }

.theme-dark .erc-ai-advisor__disclaimer {
  color: #6b7280;
  border-top-color: #e0e4ea;
}
.theme-dark .erc-ai-advisor__disclaimer-link:hover { color: #1a1d23; }

/* Suggestions — dark surface to read as a "refine your search" prompt */
.theme-dark .erc-ai-advisor__suggestions {
  background: #2a313b;
  border: 1px solid #3a4150;
}
.theme-dark .erc-ai-advisor__suggestions-label {
  color: #cfd4dc;
}
</style>
